import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { useExcelUpload } from '../../hooks/useExcelUpload';

/**
 * Generic Upload Modal Component
 * Consolidates common upload modal UI and logic
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onUploadSuccess - Success callback
 * @param {Function} props.onUploadError - Error callback
 * @param {string} props.jobNumber - Optional job number for tracking uploads
 * @param {Object} props.config - Upload configuration from UPLOAD_CONFIGS
 */
const UploadModal = React.memo(({
  isOpen,
  onClose,
  onUploadSuccess,
  onUploadError,
  jobNumber,
  estimateNumber,
  config,
}) => {
  const { instance, accounts } = useMsal();

  // Optional config-driven metadata fields collected on the preview step
  // (e.g. MTO client rev # + transmittal #). Posted alongside the parsed rows.
  const extraFields = config.extraFields || [];
  const [extraValues, setExtraValues] = useState({});

  const {
    state,
    fileInputRef,
    handleDrag,
    handleDrop,
    handleFileSelect,
    handleFileInputChange,
    resetModal,
    startUpload,
    handleUploadSuccess,
    handleUploadError,
    setValidationError,
    setLoading,
  } = useExcelUpload({
    columnMapping: config.columnMapping,
    numericColumns: config.numericColumns || [],
    dateColumns: config.dateColumns || [],
    integerColumns: config.integerColumns || ['id'],
  });

  const {
    dragActive,
    loading,
    uploadProgress,
    uploadStatus,
    parsedData,
    validationError,
    step,
  } = state;

  // Close and reset
  const handleClose = () => {
    resetModal();
    setExtraValues({});
    onClose();
  };

  // Prefer the API's message over Axios's generic "Request failed with status code NNN".
  const getUploadErrorMessage = (error, fallback = 'Failed to upload data') =>
    error?.response?.data?.message
    || error?.response?.data?.error
    || error?.message
    || fallback;

  // Handle the upload
  const handleUpload = async () => {
    if (!parsedData || parsedData.length === 0) {
      setValidationError('No data to upload');
      return;
    }

    const maxRows = config.maxRows;
    if (maxRows && parsedData.length > maxRows) {
      const msg = `This file has ${parsedData.length.toLocaleString()} rows. Import is limited to ${maxRows.toLocaleString()} rows at a time — split the file and upload in batches.`;
      setValidationError(msg);
      if (onUploadError) onUploadError(msg);
      return;
    }

    startUpload();

    try {
      // Build request body
      const body = { data: parsedData };
      if (jobNumber) {
        body.job_number = jobNumber;
      }
      if (estimateNumber) {
        body.estimate_number = estimateNumber;
      }
      // Include any config-driven extra metadata fields.
      extraFields.forEach((f) => {
        const v = extraValues[f.key] ?? f.default;
        if (v !== undefined && v !== '') body[f.key] = v;
      });

      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        config.apiEndpoint,
        body
      );

      if (response.data.success) {
        handleUploadSuccess('Upload complete!');
        
        if (onUploadSuccess) {
          // Pass the server's real result through untouched (so callers can show
          // accurate matched/skipped counts). Use ?? — not || — so a truthful 0
          // (e.g. "0 prices updated / all skipped") isn't masked as a full success.
          onUploadSuccess({
            ...response.data,
            jobId: response.data.jobId,
            rowsProcessed: response.data.rowsQueued ?? response.data.rowsProcessed ?? parsedData.length,
            message: response.data.message
          });
        }
        
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      const msg = getUploadErrorMessage(error);
      handleUploadError(msg);
      
      if (onUploadError) {
        onUploadError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Get theming from config
  const accentColor = config.accentColor || '#007bff';
  const bgLight = config.bgLight || '#f0f7ff';
  const textColor = config.textColor || accentColor;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '2rem',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ margin: 0, color: '#333' }}>
            {config.icon} {config.title}
          </h3>
          <button
            onClick={handleClose}
            disabled={loading}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              color: '#666',
              padding: '0.25rem',
              opacity: loading ? 0.5 : 1
            }}
          >
            ×
          </button>
        </div>

        {/* Instructions */}
        <div style={{
          backgroundColor: bgLight,
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '1.5rem',
          fontSize: '0.9rem',
          color: textColor
        }}>
          <strong>Upload Instructions:</strong>
          <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
            <li>Use the exported Excel file as a template</li>
            <li>Rows with an <strong>ID</strong> will be updated</li>
            <li>Rows without an ID will be inserted as new records</li>
            {jobNumber && <li>Records will be associated with Job: <strong>{jobNumber}</strong></li>}
            {config.instructions && <li>{config.instructions}</li>}
            {config.maxRows && (
              <li>Maximum <strong>{config.maxRows.toLocaleString()}</strong> rows per upload — split larger files into batches.</li>
            )}
          </ul>
        </div>

        {/* Validation Error */}
        {validationError && (
          <div style={{
            backgroundColor: '#f8d7da',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            color: '#721c24',
            fontSize: '0.9rem'
          }}>
            <strong>Error:</strong> {validationError}
          </div>
        )}

        {/* Step: Select File */}
        {step === 'select' && (
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={(e) => handleDrop(e, handleFileSelect)}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${dragActive ? accentColor : '#dee2e6'}`,
              borderRadius: '8px',
              padding: '3rem 2rem',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: dragActive ? bgLight : '#f8f9fa',
              transition: 'all 0.2s ease'
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📁</div>
            <p style={{ margin: 0, color: '#495057', fontWeight: 'bold' }}>
              {loading ? 'Parsing file...' : 'Drag & drop Excel file here'}
            </p>
            <p style={{ margin: '0.5rem 0 0 0', color: '#6c757d', fontSize: '0.9rem' }}>
              or click to browse
            </p>
          </div>
        )}

        {/* Step: Preview */}
        {step === 'preview' && parsedData && (
          <div>
            <div style={{
              backgroundColor: '#d4edda',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              color: '#155724'
            }}>
              <strong>✓ File parsed successfully!</strong>
              <p style={{ margin: '0.5rem 0 0 0' }}>
                Found <strong>{parsedData.length}</strong> rows to process
              </p>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem' }}>
                Rows with ID: {parsedData.filter(r => r.id).length} (will be updated)<br/>
                New rows: {parsedData.filter(r => !r.id).length} (will be inserted)
              </p>
            </div>

            {/* Preview Table */}
            <div style={{
              maxHeight: '200px',
              overflow: 'auto',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              marginBottom: '1rem'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                  <tr>
                    {(config.previewColumns || ['id']).map(col => (
                      <th key={col} style={{ 
                        padding: '0.5rem', 
                        borderBottom: '1px solid #dee2e6', 
                        textAlign: 'left',
                        whiteSpace: 'nowrap'
                      }}>
                        {col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parsedData.slice(0, 10).map((row, idx) => (
                    <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#f8f9fa' }}>
                      {(config.previewColumns || ['id']).map(col => (
                        <td key={col} style={{ padding: '0.5rem', borderBottom: '1px solid #eee' }}>
                          {row[col] !== null && row[col] !== undefined 
                            ? (col === 'id' && !row[col] ? '(new)' : String(row[col]).substring(0, 30))
                            : '-'
                          }
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {parsedData.length > 10 && (
                <div style={{ padding: '0.5rem', textAlign: 'center', color: '#6c757d', fontSize: '0.8rem' }}>
                  ... and {parsedData.length - 10} more rows
                </div>
              )}
            </div>

            {extraFields.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                {extraFields.map((f) => {
                  const value = extraValues[f.key] ?? f.default ?? '';
                  return (
                    <div key={f.key} style={{ marginBottom: '0.75rem' }}>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#495057', marginBottom: '0.25rem' }}>
                        {f.label}{f.required ? ' *' : ''}
                      </label>
                      {f.type === 'select' ? (
                        <select
                          value={value}
                          onChange={(e) => setExtraValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box', backgroundColor: 'white' }}
                        >
                          {(f.options || []).map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => setExtraValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
                          placeholder={f.placeholder || ''}
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                        />
                      )}
                      {f.help && <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.2rem' }}>{f.help}</div>}
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={resetModal}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Choose Different File
              </button>
              <button
                onClick={handleUpload}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: accentColor,
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                📤 Upload {parsedData.length} Rows
              </button>
            </div>
          </div>
        )}

        {/* Step: Uploading */}
        {step === 'uploading' && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{
              width: '60px',
              height: '60px',
              border: '4px solid #f3f3f3',
              borderTop: `4px solid ${accentColor}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }}></div>
            <p style={{ color: '#495057', fontWeight: 'bold' }}>{uploadStatus}</p>
            <div style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#e9ecef',
              borderRadius: '4px',
              overflow: 'hidden',
              marginTop: '1rem'
            }}>
              <div style={{
                width: `${uploadProgress}%`,
                height: '100%',
                backgroundColor: accentColor,
                transition: 'width 0.3s ease'
              }}></div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
});

export default UploadModal;